import React, { useEffect, useState } from "react";
import { Box, Grid, TextField } from "@mui/material";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import * as taskService from "../../../../models/services/taskService";
import { ISessionState } from "../../../../models/session/sessionState";
import { Translations } from "ui/Scripts/source/models/translations";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Dropdown } from "ui/Scripts/source/components/framework/Dropdown/Dropdown";
import { TaskStateListChip } from "../../TaskStateListChip";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { ITaskEditItemTaskState } from "source/models/task/taskEditItemTaskState";
import { ITaskEditItemTaskPriority } from "source/models/task/taskEditItemTaskPriority";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { TaskDynamicFormContainer } from "../../TaskDynamicFormContainer";
import { TaskDynamicForm } from "../../TaskDynamicForm/TaskDynamicForm";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { UserBlocks } from "source/components/user/UserBlocks/UserBlocks";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { AppUtils } from "source/framework/appUtils";
import { UserListItems } from "source/models/user/userListItems";
import { IToggleButtonOption, ToggleButtons } from "source/components/framework/ToggleButtons";
import { ITaskHandler, TaskHandler } from "source/models/task/taskHandler";
import { TaskHandlerType } from "ui/Scripts/source/models/enums";

interface ITaskEditDialogGeneraViewProps {
    sessionState: ISessionState;
    isNew: boolean;
    taskStateIsReadOnly: boolean;
    isLockedToOtherUser: boolean;
    companyId: string;
    taskId: string;
    name: string;
    taskTypeId: string;
    taskStateId: string;
    taskPriorityId: string;
    unitId: string;
    createUser: IModifyUser;
    taskHandlers: ITaskHandler[];
    taskType: ITaskEditTypeItem;
    taskHeaderDataCols: ITaskDataRowCol[];
    canChangeTaskHeaderData: boolean;
    canChangeTaskState: boolean;
    allTaskTypes: ITaskEditTypeItem[];
    taskTypes: ITaskEditTypeItem[];
    taskStates: ITaskEditItemTaskState[];
    taskPriorities: ITaskEditItemTaskPriority[];
    units: IIdTitleNode[];
    visibilityOptions: IToggleButtonOption[];
    taskTypeUnitIds: string[];
    onChangeName: (name: string) => void;
    onChangeHandlers: (handlers: ITaskHandler[]) => void;
    onChangeUnit: (unitId: string) => void;
    onChangeTaskTypes: (taskTypes: ITaskEditTypeItem[]) => void;
    onChangeTaskType: (taskTypeId: string, defaultUnitId?: string) => void;
    onChangeTaskState: (taskStateId: string) => void;
    onChangeTaskPriority: (taskPriorityId: string) => void;
    onChangeTaskHeaderDataCols: (taskHeaderDataCols: ITaskDataRowCol[]) => void;
}

export const TaskEditDialogGeneraView = (props: ITaskEditDialogGeneraViewProps) => {
    const [taskState, setTaskState] = useState<ITaskEditItemTaskState>(null);
    const [taskPriority, setTaskPriority] = useState<ITaskEditItemTaskPriority>(null);
    const [unit, setUnit] = useState<IIdTitleNode>(null);
    const [handlers, setHandlers] = useState<ITaskHandler[]>([]);
    const [personsInCharge, setPersonsInCharge] = useState<ITaskHandler[]>([]);

    useEffect(() => {
        setTaskState(props.taskStates.find(i => i.id === props.taskStateId) ?? props.taskStates[0]);
        setTaskPriority(props.taskPriorities.find(i => i.id === props.taskPriorityId) ?? props.taskPriorities[0]);
        setUnit(props.unitId ? IdTitleNode.getById(props.units, props.unitId) : null);
    }, [props.taskStateId, props.taskPriorityId, props.unitId]);

    useEffect(() => {
        setHandlers(props.taskHandlers.filter(i => i.taskHandlerType === TaskHandlerType.Handler));
        setPersonsInCharge(props.taskHandlers.filter(i => i.taskHandlerType === TaskHandlerType.PersonInCharge));
    }, [props.taskHandlers]);

    // #region Handlers
    const addHandlers = async (users: IModifyUser[], taskHandlerType: TaskHandlerType): Promise<ApiSuccess> => {
        if (!users || users.length < 1) return null;
        if (!props.isNew) {
            const result = await AppUtils.callService(() => taskService.taskEditAddHandlers(props.companyId, props.taskId, taskHandlerType, users.map(i => i.code)), true);
            if (!result) return null;
        }
        const newTaskHandlers = props.taskHandlers.concat(users.map(i => TaskHandler.createTaskHandler(i, taskHandlerType)));
        ModifyUser.sortModifyUsers(newTaskHandlers);
        props.onChangeHandlers(newTaskHandlers);
        return new ApiSuccess();
    };

    const handleAddHandlers = async (users: IModifyUser[], taskHandlerType: TaskHandlerType): Promise<ApiSuccess> => {
        return addHandlers(users, taskHandlerType);
    };

    const handleRemoveHandlers = async (userCodes: string[], taskHandlerType: TaskHandlerType): Promise<ApiSuccess> => {
        if (!userCodes || userCodes.length < 1) return null;
        if (!props.isNew) {
            const result = await AppUtils.callService(() => taskService.taskEditRemoveHandlers(props.companyId, props.taskId, taskHandlerType, userCodes));
            if (!result) return null;
        }
        const newTaskHandlers = props.taskHandlers.filter(i => i.taskHandlerType !== taskHandlerType || userCodes.indexOf(i.code) < 0);
        ModifyUser.sortModifyUsers(newTaskHandlers);
        props.onChangeHandlers(newTaskHandlers);
        return new ApiSuccess();
    };
    
    const handleGetHandlersSelectListItems = (_companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, _ignoreQrCodeUsers: boolean, _requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        return taskService.getHandlersSelectUserListItems(props.companyId, props.taskId, props.unitId, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode,
            sortColumn, sortOrderIsAsc);
    };
    // #endregion Handlers

    //Can only change for new task
    const handleChangePublic = (isPublic: boolean) => {
        const newTaskTypes = TaskEditTypeItem.getTaskTypesByVisibility(props.allTaskTypes, isPublic);
        const oldTaskTypeId = props.taskTypeId;
        const newTaskTypeId = newTaskTypes.find(i => i.id === oldTaskTypeId)?.id ?? newTaskTypes[0].id;
        props.onChangeTaskTypes(newTaskTypes);
        if (!isPublic) {
            props.onChangeUnit("");
            if (oldTaskTypeId === newTaskTypeId) return;
            props.onChangeTaskType(newTaskTypeId);
        } else {
            const defaultUnitId = IdTitleNode.getFirstActiveNode(props.units)?.id ?? "";
            if (oldTaskTypeId !== newTaskTypeId) {
                props.onChangeTaskType(newTaskTypeId, defaultUnitId);
                return;
            }
            const unitId = props.taskTypeUnitIds.length
                ? props.taskTypeUnitIds.find(i => i === defaultUnitId) || props.taskTypeUnitIds[0]
                : defaultUnitId;
            props.onChangeUnit(unitId);
        }
    };

    const isReadOnly = props.taskStateIsReadOnly || props.isLockedToOtherUser || !props.canChangeTaskHeaderData;
    const showVisibilityOptions = props.isNew && props.visibilityOptions.length > 1;

    return (
        <Box sx={{ maxHeight: "calc(100vh - 500px)", overflowY: "auto", overflowX: "hidden" }}>
            <GridRow sx={{ position: "relative" }}>
                <Grid item sm={8}>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.TaskType} margin={0}>
                                {(!props.isNew || props.taskTypes.length < 2
                                    ? <TextField title={Translations.TaskType} value={props.taskType.code} disabled={true} size="small" fullWidth />
                                    : <Dropdown
                                        selectedTitle={props.taskType.code}
                                        selectMode={true}
                                        actions={props.taskTypes.map(item => {
                                            return {
                                                title: item.code,
                                                selected: props.taskTypeId === item.id,
                                                onClick: () => props.onChangeTaskType(item.id)
                                            };
                                        })}
                                    />
                                )}
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.State} margin={0}>
                                <Dropdown
                                    selectedTitle={<TaskStateListChip code={taskState?.code} color={taskState?.color} fullWidth={true} />}
                                    selectMode={true}
                                    disabled={props.isLockedToOtherUser || (!props.canChangeTaskHeaderData && !props.canChangeTaskState)}
                                    actions={props.taskStates.map(item => {
                                        return {
                                            title: <TaskStateListChip code={item.code} color={item.color} fullWidth={true} />,
                                            selected: props.taskStateId === item.id,
                                            onClick: () => props.onChangeTaskState(item.id)
                                        };
                                    })}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.Priority}>
                                <Dropdown
                                    selectedTitle={taskPriority?.code}
                                    selectMode={true}
                                    disabled={isReadOnly}
                                    actions={props.taskPriorities.map(item => {
                                        return {
                                            title: item.code,
                                            selected: props.taskPriorityId === item.id,
                                            onClick: () => props.onChangeTaskPriority(item.id)
                                        };
                                    })}
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            {!unit && !showVisibilityOptions &&
                                <FormField title={Translations.Visibility}>
                                    <TextField title={Translations.Visibility} value={Translations.Private} disabled={true} size="small" fullWidth />
                                </FormField>
                            }
                            {!!unit &&
                                <FormField title={Translations.Unit}>
                                    <TreeCheckboxDropdown
                                        required={true}
                                        disabled={isReadOnly}
                                        singleSelect={true}
                                        selectedIds={[props.unitId]}
                                        showCommentInDropdown={true}
                                        values={props.units}
                                        onChange={(unitIds: string[]) => props.onChangeUnit(unitIds[0])}
                                        onGetTitleSuffix={props.taskTypeUnitIds.length > 0
                                            ? (id: string) => {
                                                if (props.taskTypeUnitIds.includes(id)) return null;
                                                return <Box component="span" sx={{ color: "error.main" }} > - {Translations.NotUsableForSelectedTaskType}</Box>;
                                            }
                                            : null
                                        }
                                    />
                                </FormField>
                            }
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Title} required>
                                <TextField name="code" title={Translations.Title} inputProps={{ maxLength: 100 }} value={props.name} disabled={isReadOnly} required={true} onChange={(event) => props.onChangeName(event.target.value)} size="small" fullWidth />
                            </FormField>
                        </Grid>
                    </GridRow>
                </Grid>
                <Grid item sm={4}>
                    {showVisibilityOptions &&
                        <FormField title={Translations.Visibility} margin={0} fullWidth>
                            <ToggleButtons
                                value={unit ? "1" : "0"}
                                options={props.visibilityOptions}
                                onChange={(value) => handleChangePublic(value === "1")}
                            />
                        </FormField>
                    }
                    {!!unit &&
                        <>
                            <UserBlocks
                                margin={!showVisibilityOptions ? "0" : undefined}
                                companyId={props.companyId}
                                isReadOnly={isReadOnly}
                                title={Translations.PersonsInCharge}
                                users={personsInCharge}
                                removeUsersConfirmation={Translations.PersonsInChargeRemoveConfirmation}
                                onAddUsers={(users: IModifyUser[]) => handleAddHandlers(users, TaskHandlerType.PersonInCharge)}
                                onRemoveUsers={(userCodes: string[]) => handleRemoveHandlers(userCodes, TaskHandlerType.PersonInCharge)}
                                onGetUserSelectListItems={handleGetHandlersSelectListItems}
                            />
                            <UserBlocks
                                companyId={props.companyId}
                                isReadOnly={isReadOnly}
                                title={Translations.TaskHandlers}
                                noUsersMessage={Translations.AllUsersOfTheUnit}
                                noUsersMessageUseInReadOnly={true}
                                fixedIgnoreUserCodes={props.createUser ? [props.createUser.code] : undefined}
                                fixedUsers={handlers.length > 0 && props.createUser ? [props.createUser] : undefined}
                                users={handlers}
                                removeUsersConfirmation={Translations.PersonsRemoveConfirmation}
                                onAddUsers={(users: IModifyUser[]) => handleAddHandlers(users, TaskHandlerType.Handler)}
                                onRemoveUsers={(userCodes: string[]) => handleRemoveHandlers(userCodes, TaskHandlerType.Handler)}
                                onGetUserSelectListItems={handleGetHandlersSelectListItems}
                            />
                        </>
                    }
                </Grid>
            </GridRow>
            {props.taskHeaderDataCols.length > 0 &&
                <Box sx={{ marginTop: "1rem" }}>
                    <TaskDynamicFormContainer>
                        <TaskDynamicForm
                            isReadOnly={isReadOnly}
                            isEdit={!props.isNew}
                            taskTypeCols={props.taskType.headerCols}
                            taskDataRowCols={props.taskHeaderDataCols}
                            onRefreshCols={props.onChangeTaskHeaderDataCols}
                        />
                    </TaskDynamicFormContainer>
                </Box>
            }
        </Box>
    );
};
